import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import GtmService from '../../../services/gtm.service';
import { mapCartToVoyadoEngageCart } from '../../../services/mappers/voyadoEngageWebTracking.mapper';
import { Ga4Events } from '../../../services/models/gtm.models';
import SearchService from '../../../services/search.service';
import { VoyadoEngageWebTrackingService } from '../../../services/voyadoEngageWebTracking.service';
import { useProductPageStore } from '../../../stores/productPageStore';
import { API_ERROR_MESSAGES, API_ERROR_NAMES, USER_FEEDBACK } from '../../../utils/API/APIErrors';
import {
  addDiscountCode as addDiscountCodeApi,
  addProductToCart,
  removeDiscountCode as removeDiscountCodeApi,
  removeProductFromCart,
  updateProductQuantity as updateProductQuantityApi,
} from '../../../utils/API/Cart/cartAPI';
import {
  Cart,
  CheckoutLoginCta,
  KeySellingPoint,
  LineItemModel,
} from '../../../utils/API/Cart/cartAPISchema';
import { handleGtmTracking } from '../../../utils/API/GTM';
import * as CheckoutHelpers from '../../../utils/checkoutHelpers';
import { formatWithoutRounding } from '../../../utils/productPrice';

interface PaymentLogos {
  id: string;
  url: string;
  alt: string;
}

export const useShoppingCartStore = defineStore('shoppingCart', () => {
  // State
  const cart = ref<Cart | null | undefined>(null);
  const ksps = ref<KeySellingPoint[] | null | undefined>(null);
  const checkoutLoginCta = ref<CheckoutLoginCta | null | undefined>(null);
  const discounts = ref<string[] | null | undefined>([]);
  const lineItemErrors = ref<Record<string, string> | null | undefined>({});
  const summaryError = ref<string | null | undefined>('');
  const discountError = ref<string | null | undefined>('');
  const paymentLogos = ref<PaymentLogos[]>([]);
  const stockSummaryVisible = ref(false);

  // Getters
  const esalesKeys = computed(() => cart.value?.lineItems.map((li) => li.esalesKey) ?? []);

  // A line item is only "out of stock" when it has zero inventory. Partial
  // availability (0 < currentInventory < quantity) is still "in stock" for
  // display purposes — it stays in the normal list with its quantity untouched,
  // and any communication about the shortfall (e.g. a stock-status pill) is
  // layered on top rather than removing the item from the cart.
  const isInStock = (lineItem: LineItemModel) => lineItem.currentInventory > 0;

  const lineItemsInStock = computed(() => cart.value?.lineItems.filter(isInStock));

  const lineItemsOutOfStock = computed(() =>
    cart.value?.lineItems.filter((lineItem) => !isInStock(lineItem))
  );

  // Covers both fully out-of-stock and partial-stock line items — anything
  // where the requested quantity can't be fully satisfied. No explicit
  // "clear" action is needed: callers render the summary behind
  // `stockSummaryVisible && insufficientStockLineItems.length`, so it
  // disappears on its own once this list empties (e.g. after the customer
  // adjusts quantities), mirroring `customerInformationStore`'s
  // `errorSummaryVisible && errorsExist` pattern.
  const insufficientStockLineItems = computed(
    () => cart.value?.lineItems.filter((lineItem) => lineItem.currentInventory < lineItem.quantity) ?? []
  );

  const freeShipping = computed(() => {
    if (!cart.value) return false;
    return CheckoutHelpers.qualifiesForFreeShipping(
      cart.value.freeShippingActive,
      cart.value.freeShippingLimit,
      cart.value.subTotal
    );
  });

  const shippingCallToActionText = computed(() => {
    const remainingAmount = CheckoutHelpers.amountNeededToQualifyForFreeShipping(
      cart.value?.freeShippingActive,
      cart.value?.freeShippingLimit,
      cart.value?.subTotal
    )?.amount;
    if (!remainingAmount) return '';
    return `Du er ${formatWithoutRounding(remainingAmount)} unna fri frakt`;
  });

  const currentCartSize = computed(
    () => lineItemsInStock.value?.reduce((acc, lineItem) => acc + lineItem.quantity, 0) || 0
  );

  const hasItemsInCart = computed(() => (cart.value?.lineItems.length ?? 0) > 0);

  // Actions
  function setCart(c: Cart | null | undefined) {
    cart.value = c;
  }
  function setKsps(k: KeySellingPoint[] | null | undefined) {
    ksps.value = k;
  }
  function setCheckoutLoginCta(c: CheckoutLoginCta | null | undefined) {
    checkoutLoginCta.value = c;
  }
  function setLineItemError(ean: string, error: string) {
    lineItemErrors.value = { ...lineItemErrors.value, [ean]: error };
  }
  function setLineItemErrors(errors: Record<string, string> | null | undefined) {
    lineItemErrors.value = errors;
  }
  function setSummaryError(error: string | null | undefined) {
    summaryError.value = error;
  }
  function setDiscountError(error: string | null | undefined) {
    discountError.value = error;
  }
  function setPaymentLogos(logos: PaymentLogos[]) {
    paymentLogos.value = logos;
  }

  async function addDiscountCode(discountCode: string): Promise<void> {
    try {
      const data = await addDiscountCodeApi(discountCode);
      setCart(data);
    } catch (error) {
      const errorStr = error as string;
      discountError.value = errorStr;
      return;
    }
    if (discounts.value) {
      discounts.value = [...discounts.value, discountCode];
    }
    discountError.value = '';
  }

  async function removeDiscountCode(discountCode: string): Promise<void> {
    try {
      const data = await removeDiscountCodeApi(discountCode);
      setCart(data);
    } catch (error) {
      const errorStr = error as string;
      if (errorStr === USER_FEEDBACK.GENERIC) {
        discountError.value = errorStr;
      }
      return;
    }
    if (discounts.value) {
      discounts.value = discounts.value.filter((d) => d !== discountCode);
    }
    discountError.value = '';
  }

  async function addProduct(
    code: string,
    isQuickAdd: boolean,
    itemListId: string,
    itemListName: string,
    apptusTicket: string,
    warehouseCode?: string
  ): Promise<{ success: boolean; errorMessage?: string; isStockError?: boolean }> {
    console.log("Adding product to cart with code:", code);

    const productPageStore = useProductPageStore();
    try {
      const data = await addProductToCart(code, warehouseCode);
      if (data.updateCartError) {
        console.log("Error adding product to cart:", data.errorMessage);
        return { success: false, errorMessage: data.errorMessage || USER_FEEDBACK.GENERIC, isStockError: data.isStockError };
      }
      setCart(data.cartDto);
      productPageStore.setCartItemCount(data.cartDto!.itemCount);
      const lineItem = cart.value?.lineItems.find((x) => x.code === code) as LineItemModel;
      if (isQuickAdd) {
        GtmService.pushCheckoutEvent(Ga4Events.AddToCartFromList, lineItem, {
          item_list_id: itemListId.toLowerCase(),
          item_list_name: itemListName.toLowerCase(),
        });
      } else {
        GtmService.pushCheckoutEvent(Ga4Events.AddToCart, lineItem);
      }
      if (apptusTicket) {
        SearchService.notifyAddToCart(apptusTicket);
      }
      VoyadoEngageWebTrackingService.trackCart(mapCartToVoyadoEngageCart(data.cartDto!));
      return { success: true };
    } catch (error) {
      return { success: false, errorMessage: USER_FEEDBACK.GENERIC };
    }
  }

  async function removeProduct(code: string, warehouseCode?: string): Promise<void> {
    const productPageStore = useProductPageStore();
    const lineItem = cart.value?.lineItems.find((x) => x.code === code) as LineItemModel;
    const checkoutEventModel = await GtmService.getCheckoutEventModel(
      Ga4Events.RemoveFromCart,
      lineItem?.code
    );
    try {
      const data = await removeProductFromCart(code, warehouseCode);
      setCart(data.cartDto);
      productPageStore.setCartItemCount(data.cartDto!.itemCount);
      if (checkoutEventModel) {
        handleGtmTracking(checkoutEventModel);
      }
      VoyadoEngageWebTrackingService.trackCart(mapCartToVoyadoEngageCart(data.cartDto!));
    } catch (error) {
      if (!lineItem || !cart.value) return;
      setLineItemError(lineItem.ean, USER_FEEDBACK.GENERIC);
    }
  }

  async function updateProductQuantity(
    code: string,
    ean: string,
    quantity: number,
    isAdditive: boolean,
    signal: AbortSignal,
    warehouseCode?: string
  ): Promise<{ success: boolean; errorMessage?: string }> {
    const lineItem = cart.value?.lineItems.find((x) => x.ean === ean) as LineItemModel;
    try {
      const data = await updateProductQuantityApi(code, quantity, isAdditive, signal, warehouseCode);

      if (data.updateCartError) {
        return { success: false, errorMessage: data.errorMessage || USER_FEEDBACK.GENERIC };
      }

      if (lineItemErrors.value?.[lineItem.ean]) {
        const { [lineItem.ean]: _, ...rest } = lineItemErrors.value;
        setLineItemErrors(rest);
      }
      setCart(data.cartDto);
      GtmService.pushCheckoutEvent(quantity > 0 ? Ga4Events.AddToCart : Ga4Events.RemoveFromCart);
      VoyadoEngageWebTrackingService.trackCart(mapCartToVoyadoEngageCart(data.cartDto!));
      return { success: true };
    } catch (error) {
      if (!(error instanceof Error)) return { success: false, errorMessage: USER_FEEDBACK.GENERIC };
      if (error.name === API_ERROR_NAMES.ABORT_ERROR) return { success: true };
      if (!lineItem || !cart.value) return { success: false, errorMessage: USER_FEEDBACK.GENERIC };
      if (error.message === API_ERROR_MESSAGES.CART_FULL) {
        setSummaryError(USER_FEEDBACK.CART_FULL);
        return { success: false, errorMessage: USER_FEEDBACK.CART_FULL };
      }
      return { success: false, errorMessage: USER_FEEDBACK.GENERIC };
    }
  }

  function clear() {
    cart.value = null;
    ksps.value = null;
    checkoutLoginCta.value = null;
    discounts.value = [];
    lineItemErrors.value = {};
    summaryError.value = null;
    discountError.value = null;
    paymentLogos.value = [];
  }

  return {
    cart,
    ksps,
    checkoutLoginCta,
    discounts,
    lineItemsOutOfStock,
    lineItemErrors,
    summaryError,
    discountError,
    paymentLogos,
    esalesKeys,
    lineItemsInStock,
    insufficientStockLineItems,
    stockSummaryVisible,
    freeShipping,
    shippingCallToActionText,
    currentCartSize,
    hasItemsInCart,
    setCart,
    setKsps,
    setCheckoutLoginCta,
    setLineItemError,
    setLineItemErrors,
    setSummaryError,
    setDiscountError,
    setPaymentLogos,
    addDiscountCode,
    removeDiscountCode,
    addProduct,
    removeProduct,
    updateProductQuantity,
    clear,
  };
});
